import type { ChangeEvent, KeyboardEvent } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import type { Theme } from '@material-ui/core';
import { InputBase } from '@material-ui/core';

import { Block } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { stripHtml } from '@klp/utils/stringUtils';
import classNames from 'classnames';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';

interface IProps {
    searchHandler: (search: string) => void;
    placeholder: string;
    searchOnType?: boolean;
    searchResult?: string;
    keyDownHandler?: () => void;
    focused?: boolean;
}

export const highlightSearch = (
    value: string | undefined,
    search: string,
): string => {
    return stripHtml(value || '').replace(
        new RegExp(search, 'gi'),
        (str: string) => (search.length > 0 ? `<b>${str}</b>` : str),
    );
};

export const useMemoizedFilter = <T,>(
    source: Array<T>,
    search: string,
    fields: Array<string>,
) => {
    return useMemo(
        () => filterSearchResult(source, search, fields),
        [source, search, fields],
    );
};

export const filterSearchResult = <T,>(
    source: Array<T>,
    search: string,
    fields: Array<string>,
) => matchSearchPhrase<T>(source, search, fields);

export const matchSearchPhrase = <T,>(
    source: Array<T>,
    search: string,
    fields: Array<string>,
) => {
    return source.filter((row) =>
        fields.some((field) => {
            return row[field]
                ? stripHtml(row[field].toString().toLowerCase()).includes(
                      search.toLowerCase(),
                  )
                : false;
        }),
    );
};

const styles = (theme: Theme) => ({
    placeholder: {
        border: '1px solid transparent',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        display: 'flex',
        minWidth: '16rem',
        alignItems: 'center',
        '& > *': {
            display: 'flex',
            '&:first-child': {
                flex: '1 1 auto',
            },
            '&:last-child': {
                cursor: 'pointer',
                '&:hover svg': {
                    fill: theme.palette.secondary.main,
                },
            },
        },
        '& input': {
            fontSize: '0.9em',
        },
    },

    focused: {
        border: `2px solid ${theme.palette.primary.main}`,
        animation: '$shake 0.5s ease-in-out',
        transition: 'border 0.3s ease-in-out',
    },
    '@keyframes shake': {
        '0%, 100%': {
            transform: 'translateX(0)',
        },
        '25%, 75%': {
            transform: 'translateX(-5px)',
        },
        '50%': {
            transform: 'translateX(5px)',
        },
    },
});

export type SearchProps = IProps;

export const Search: React.FC<SearchProps> = (props: SearchProps) => {
    const {
        searchHandler,
        placeholder,
        searchOnType = false,
        searchResult = '',
        keyDownHandler,
        focused = false,
    } = props;
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const searchRef = useRef<HTMLInputElement | null>(null);
    const klpStyles = useKlpStyles({}, styles);

    // Focus input when `focused` is true
    useEffect(() => {
        if (focused) {
            searchRef.current?.focus();
        }
    }, [focused]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);

        if (searchOnType) {
            searchHandler(value);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            searchHandler(e.currentTarget.value);
        } else if (e.key === 'ArrowDown' && keyDownHandler) {
            keyDownHandler();
        }
    };

    const handleSearchIconClick = () => {
        if (searchOnType) return;
        searchHandler(search);
    };

    return (
        <Block
            className={classNames(klpStyles.placeholder, {
                [klpStyles.focused]: focused,
            })}
            data-testid="Search"
        >
            <InputBase
                value={searchResult || search}
                inputRef={searchRef}
                className="Search__Search"
                placeholder={t(placeholder)}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                data-testid="SearchInput"
                autoFocus={focused}
            />
            <Illustration
                icon={IconName.SEARCH}
                data-testid="SearchButton"
                onClick={handleSearchIconClick}
            />
        </Block>
    );
};
